import { useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@design-system';
import { docusignRoles } from '@shared/docusignRoles';
import { useTemplateConfigForm } from '@/features/quote-and-apply-builder/hooks/use-template-config-form';
import { TemplateConditionBuilder } from '@/features/quote-and-apply-builder/components/template-condition-builder';

const roleEntries = Object.entries(docusignRoles) as [
  DocusignRoleKey,
  (typeof docusignRoles)[DocusignRoleKey],
][];

function AllowedRolesSelector({
  allowedRoles,
  onToggleRole,
}: {
  allowedRoles: string[];
  onToggleRole: (role: string) => void;
}) {
  const [filter, setFilter] = useState('');

  const filteredRoles = useMemo(() => {
    if (!filter) return roleEntries;
    const lower = filter.toLowerCase();
    return roleEntries.filter(
      ([key, role]) =>
        key.toLowerCase().includes(lower) || role.roleName.toLowerCase().includes(lower),
    );
  }, [filter]);

  return (
    <div className="space-y-3">
      <Label>Allowed Roles</Label>
      <p className="text-muted-foreground text-xs">
        DocuSign signer roles that can see this template. Leave none selected for all roles.
      </p>
      <Input
        value={filter}
        onChange={e => setFilter(e.target.value)}
        placeholder="Filter roles..."
      />
      <ScrollArea className="h-36 rounded-md border p-1">
        {filteredRoles.map(([key, role]) => (
          <Label
            key={key}
            className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 font-normal"
          >
            <Checkbox
              checked={allowedRoles.includes(key)}
              onCheckedChange={() => onToggleRole(key)}
            />
            <div className="min-w-0 flex-1">
              <span className="text-sm font-medium">{role.roleName}</span>
              <span className="text-muted-foreground ml-1.5 text-xs">{key}</span>
            </div>
          </Label>
        ))}
        {filteredRoles.length === 0 && (
          <p className="text-muted-foreground py-4 text-center text-sm">No matching roles</p>
        )}
      </ScrollArea>
      {allowedRoles.length > 0 && (
        <p className="text-muted-foreground text-xs">{allowedRoles.length} selected</p>
      )}
    </div>
  );
}

interface TemplateConfigFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  questions: Record<string, PolicyFormQuestion>;
  config?: PolicyApplicationTemplateConfiguration | null;
}

export function TemplateConfigFormDialog({
  open,
  onOpenChange,
  formId,
  questions,
  config,
}: TemplateConfigFormDialogProps) {
  const handleClose = () => onOpenChange(false);

  const form = useTemplateConfigForm({
    formId,
    config,
    onClose: handleClose,
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent className="max-h-[95vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {form.isEditing ? 'Edit Composite Template' : 'Create Composite Template'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="templateName">Template Name *</Label>
            <Input
              id="templateName"
              value={form.templateName}
              onChange={e => form.setTemplateName(e.target.value)}
              placeholder="e.g. securian-app-part-1-insured-info"
            />
            <p className="text-muted-foreground text-xs">
              A unique identifier for this template within the form. Must match a DocuSign template
              name.
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="defaultSequence">Sequence</Label>
              <Input
                id="defaultSequence"
                type="number"
                min={1}
                value={form.defaultSequence}
                onChange={e => form.setDefaultSequence(Number(e.target.value))}
              />
              <p className="text-muted-foreground text-xs">Order in the envelope</p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select
                value={form.status}
                onValueChange={value => form.setStatus(value as 'active' | 'inactive')}
              >
                <SelectTrigger
                  id="status"
                  className="w-full"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="isRequired"
              checked={form.isRequired}
              onCheckedChange={checked => form.setIsRequired(checked === true)}
            />
            <Label
              htmlFor="isRequired"
              className="text-sm"
            >
              This template is required
            </Label>
          </div>

          <Separator />

          <AllowedRolesSelector
            allowedRoles={form.allowedRoles}
            onToggleRole={form.handleToggleRole}
          />

          <Separator />

          <TemplateConditionBuilder
            conditional={form.conditional}
            questions={questions}
            onAddRule={form.handleAddRule}
            onUpdateRule={form.handleUpdateRule}
            onRemoveRule={form.handleRemoveRule}
            onSetLogic={form.handleSetLogic}
          />
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={form.isSaving}
          >
            Cancel
          </Button>
          <Button
            onClick={form.handleSave}
            disabled={!form.canSave || form.isSaving}
          >
            {form.isSaving ? 'Saving...' : form.isEditing ? 'Update Template' : 'Create Template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
